import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { Link } from 'react-scroll';
import { useRef } from 'react';

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.25, 0.1, 0.25, 1] },
});

const stats = [
  { num: '5+', label: 'Projects Delivered' },
  { num: '4', label: 'E-com Websites' },
  { num: '4', label: 'Businesses Managed' },
];

/* Magnetic button — moves slightly toward cursor */
function MagneticBtn({ children, style, onClick, href, target }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 18 });
  const sy = useSpring(y, { stiffness: 200, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * 0.28);
    y.set((e.clientY - r.top - r.height / 2) * 0.28);
  };
  const onLeave = () => { x.set(0); y.set(0); };

  const Tag = href ? 'a' : motion.button;
  return (
    <motion.div ref={ref} style={{ x: sx, y: sy, display: 'inline-block' }} onMouseMove={onMove} onMouseLeave={onLeave}>
      <Tag
        href={href} target={target} rel={target === '_blank' ? 'noreferrer' : undefined}
        onClick={onClick}
        data-hover
        style={{ display: 'inline-block', cursor: 'none', textDecoration: 'none', ...style }}
      >
        {children}
      </Tag>
    </motion.div>
  );
}

/* Tilt card on hover */
function TiltPhoto({ children }) {
  const ref = useRef(null);
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const srx = useSpring(rotateX, { stiffness: 120, damping: 20 });
  const sry = useSpring(rotateY, { stiffness: 120, damping: 20 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    rotateX.set(-py * 8);
    rotateY.set(px * 8);
  };
  const onLeave = () => { rotateX.set(0); rotateY.set(0); };

  return (
    <motion.div
      ref={ref}
      style={{ rotateX: srx, rotateY: sry, transformStyle: 'preserve-3d', transformPerspective: 800 }}
      onMouseMove={onMove} onMouseLeave={onLeave}
    >
      {children}
    </motion.div>
  );
}

export default function Hero() {
  return (
    <section id="home" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', position: 'relative', padding: '130px 80px 100px', overflow: 'hidden' }}>

      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 55% 70% at 75% 40%, rgba(201,169,110,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 35% 35% at 10% 75%, rgba(201,169,110,0.03) 0%, transparent 65%)', pointerEvents: 'none' }} />

      {/* Animated floating particles */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        {[...Array(6)].map((_, i) => (
          <motion.div key={i}
            animate={{ y: [0, -30, 0], opacity: [0.15, 0.4, 0.15] }}
            transition={{ duration: 4 + i * 0.8, delay: i * 0.6, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute',
              width: i % 2 === 0 ? '2px' : '1px',
              height: i % 2 === 0 ? '2px' : '1px',
              borderRadius: '50%',
              background: '#c9a96e',
              left: `${12 + i * 14}%`,
              top: `${25 + (i % 3) * 20}%`,
            }}
          />
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 480px', gap: '60px', alignItems: 'center', width: '100%', position: 'relative', zIndex: 2 }} className="hero-inner">

        {/* LEFT */}
        <div>
          <motion.div {...fade(0.15)} style={{ display: 'inline-flex', alignItems: 'center', gap: '14px', marginBottom: '44px' }}>
            <motion.span
              animate={{ scaleX: [0.3, 1, 0.3] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              style={{ display: 'block', width: '38px', height: '1px', background: '#c9a96e', originX: 0 }}
            />
            <span style={{ fontSize: '11px', letterSpacing: '0.32em', textTransform: 'uppercase', color: '#c9a96e' }}>
              Web Developer · Digital Solutions Specialist
            </span>
          </motion.div>

          <motion.h1 {...fade(0.35)} style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(60px, 8.5vw, 124px)', fontWeight: 300, lineHeight: 0.9, letterSpacing: '-0.025em', marginBottom: '12px' }}>
            <span style={{ display: 'block', overflow: 'hidden' }}>
              <motion.span style={{ display: 'block' }} initial={{ y: '100%' }} animate={{ y: 0 }} transition={{ duration: 0.8, delay: 0.35, ease: [0.76, 0, 0.24, 1] }}>
                Vimal
              </motion.span>
            </span>
            <span style={{ display: 'block', overflow: 'hidden' }}>
              <motion.span style={{ display: 'block', color: '#c9a96e', fontStyle: 'italic' }} initial={{ y: '100%' }} animate={{ y: 0 }} transition={{ duration: 0.8, delay: 0.5, ease: [0.76, 0, 0.24, 1] }}>
                Raj
              </motion.span>
            </span>
          </motion.h1>

          <motion.div {...fade(0.5)} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '36px 0 28px' }}>
            {['Web App Developer', 'ERP Systems', 'E-commerce', 'VPS Deployment', 'Digital Marketing'].map((role, i) => (
              <motion.span key={role}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.55 + i * 0.07 }}
                style={{ padding: '5px 14px', border: '1px solid rgba(201,169,110,0.25)', fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(245,240,232,0.55)', transition: 'all .3s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#c9a96e'; e.currentTarget.style.color = '#c9a96e'; e.currentTarget.style.background = 'rgba(201,169,110,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(201,169,110,0.25)'; e.currentTarget.style.color = 'rgba(245,240,232,0.55)'; e.currentTarget.style.background = 'transparent'; }}
              >
                {role}
              </motion.span>
            ))}
          </motion.div>

          <motion.p {...fade(0.6)} style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', fontWeight: 300, color: 'rgba(245,240,232,0.65)', maxWidth: '520px', lineHeight: 1.75, marginBottom: '60px' }}>
            Building production-ready web systems — from scalable React applications and ERP platforms to full deployment on live servers.
          </motion.p>

          <motion.div {...fade(0.75)} style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* View Projects — scroll */}
            <Link to="projects" smooth duration={800} offset={-80}>
              <MagneticBtn style={{
                padding: '14px 44px', background: '#c9a96e', color: '#080808',
                fontSize: '11px', letterSpacing: '0.22em', textTransform: 'uppercase',
                fontWeight: 700, fontFamily: "'Syne', sans-serif", border: 'none',
                transition: 'background .3s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#e8c98a'}
                onMouseLeave={e => e.currentTarget.style.background = '#c9a96e'}
              >
                View Projects
              </MagneticBtn>
            </Link>

            {/* Hire Me — opens resume PDF */}
            <MagneticBtn
              href="/resume.pdf"
              target="_blank"
              style={{
                fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase',
                color: 'rgba(245,240,232,0.55)', display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '12px 0', transition: 'color .3s',
                fontFamily: "'Syne', sans-serif",
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#c9a96e'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(245,240,232,0.55)'}
            >
              {/* PDF icon */}
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
                <rect x="2" y="1" width="9" height="12" rx="1"/>
                <path d="M5 1v4h4"/>
                <path d="M5 9h4M5 11h2"/>
              </svg>
              Hire Me — Resume
            </MagneticBtn>
          </motion.div>
        </div>

        {/* RIGHT — Photo + Stats */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}
          className="hero-right"
        >
          <TiltPhoto>
            <div style={{ position: 'relative' }}>
              <motion.div
                animate={{ opacity: [0.18, 0.35, 0.18] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                style={{ position: 'absolute', top: '-18px', right: '-18px', width: '100%', height: '100%', border: '1px solid #c9a96e', zIndex: 0, pointerEvents: 'none' }}
              />
              <div style={{ width: '100%', aspectRatio: '4/5', background: '#111', border: '1px solid rgba(201,169,110,0.12)', position: 'relative', overflow: 'hidden', zIndex: 1 }}>
                {/* ✅ YOUR PHOTO — keep your existing img tag */}
                <img src="/vimal.webp" alt="Vimal Raj" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(to right, transparent, #c9a96e, transparent)' }} />
              </div>
              <div style={{ position: 'absolute', bottom: '-18px', left: '20px', background: '#c9a96e', padding: '9px 22px', zIndex: 2 }}>
                <span style={{ fontSize: '10px', letterSpacing: '0.22em', textTransform: 'uppercase', color: '#080808', fontWeight: 700, fontFamily: "'Syne', sans-serif" }}>Vimal Raj</span>
              </div>
            </div>
          </TiltPhoto>

          {/* Stats */}
          <div style={{ display: 'flex', gap: '0', marginTop: '20px', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '28px' }}>
            {stats.map((s, i) => (
              <motion.div key={s.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 + i * 0.12 }}
                style={{ flex: 1, textAlign: 'center', borderRight: i < stats.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none', padding: '0 12px' }}
              >
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '40px', fontWeight: 300, color: '#c9a96e', lineHeight: 1 }}>{s.num}</div>
                <div style={{ fontSize: '8px', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(245,240,232,0.4)', marginTop: '6px', lineHeight: 1.4 }}>{s.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

      </div>

      <style>{`
        @media (max-width: 1000px) {
          .hero-inner { grid-template-columns: 1fr !important; gap: 50px !important; }
          .hero-right { flex-direction: row !important; align-items: flex-start !important; gap: 24px !important; }
          .hero-right > div:first-child { flex: 1; max-width: 260px; }
          .hero-right > div:last-child { flex: 1; flex-direction: column !important; margin-top: 0 !important; padding-top: 0 !important; border-top: none !important; border-left: 1px solid rgba(255,255,255,0.06); padding-left: 24px !important; justify-content: center; }
          section#home { padding: 110px 24px 80px !important; }
        }
        @media (max-width: 600px) {
          .hero-right { flex-direction: column !important; }
          .hero-right > div:first-child { max-width: 100% !important; width: 100%; }
          .hero-right > div:last-child { flex-direction: row !important; border-left: none !important; border-top: 1px solid rgba(255,255,255,0.06) !important; padding-left: 0 !important; padding-top: 24px !important; margin-top: 0 !important; }
        }
      `}</style>
    </section>
  );
}
